import { useEffect, useState } from 'react'
import ContentSets from './ContentSets.jsx'
import { useTranslation } from 'react-i18next'

import { api } from './api'
import Button from './Button.jsx'

/**
 * The business profile: who this shop is, and how its pictures are made.
 *
 * Three cards rather than one, because these are three different decisions and a single ribbon of
 * fourteen controls is a wall — nobody reads down it, they hunt. Who you are is a conversation
 * about the business; how the pictures look is a matter of taste; what each post carries is a
 * matter of money. One form underneath all three, and one Save, which follows down the page so it
 * is never a scroll away from whatever was just changed.
 */
export default function Profile({ onPostsWritten }) {
  const { t } = useTranslation()
  const [form, setForm] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [dirty, setDirty] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    api
      .profile()
      .then(setForm)
      .catch((failure) => setError(failure.message))
  }, [])

  function change(field, value) {
    setForm((current) => ({ ...current, [field]: value }))
    setDirty(true)
    setSaved(false)
  }

  async function save() {
    setBusy(true)
    setError(null)
    try {
      await api.saveProfile(form)
      setDirty(false)
      setSaved(true)
    } catch (failure) {
      setError(failure.message)
    } finally {
      setBusy(false)
    }
  }

  if (!form) {
    return (
      <section className="card">
        <h2>{t('profile.title')}</h2>
        {error ? <p className="status status--bad">{error}</p> : <p className="muted">{t('status.loading')}</p>}
      </section>
    )
  }

  /** A checkbox that reads as a setting: the label first, the switch on the right, one rule under it. */
  const toggle = (field, fallback) => (
    <label className="setting">
      <span className="setting__text">
        <span className="setting__name">{t(`profile.${field === 'includeStories' ? 'stories' : 'videos'}`)}</span>
        <span className="muted small">
          {t(`profile.${field === 'includeStories' ? 'storiesHelp' : 'videosHelp'}`)}
        </span>
      </span>
      <input
        type="checkbox"
        checked={form[field] ?? fallback}
        onChange={(event) => change(field, event.target.checked)}
      />
    </label>
  )

  return (
    <>
      {/* ------------------------------------------------ who the business is */}
      <section className="card">
        <h2>{t('profile.title')}</h2>
        <p className="muted">{t('profile.help')}</p>

        <div className="fields fields--two">
          <label className="field">
            {t('profile.name')}
            <input
              type="text"
              maxLength={200}
              value={form.name ?? ''}
              onChange={(event) => change('name', event.target.value)}
            />
          </label>

          <label className="field">
            {t('profile.industry')}
            <input
              type="text"
              maxLength={120}
              placeholder={t('profile.industryHint')}
              value={form.industry ?? ''}
              onChange={(event) => change('industry', event.target.value)}
            />
          </label>

          <label className="field">
            {t('profile.audience')}
            <input
              type="text"
              maxLength={300}
              placeholder={t('profile.audienceHint')}
              value={form.audience ?? ''}
              onChange={(event) => change('audience', event.target.value)}
            />
          </label>

          <label className="field">
            {t('profile.tone')}
            <input
              type="text"
              maxLength={300}
              placeholder={t('profile.toneHint')}
              value={form.brandTone ?? ''}
              onChange={(event) => change('brandTone', event.target.value)}
            />
          </label>
        </div>

        <label className="field">
          {t('profile.keyFacts')}
          <span className="muted small">{t('profile.keyFactsHelp')}</span>
          <textarea
            rows={4}
            maxLength={1000}
            placeholder={t('profile.keyFactsHint')}
            value={form.keyFacts ?? ''}
            onChange={(event) => change('keyFacts', event.target.value)}
          />
        </label>

        <label className="field">
          {t('profile.avoid')}
          <span className="muted small">{t('profile.avoidHelp')}</span>
          <textarea
            rows={3}
            maxLength={500}
            placeholder={t('profile.avoidHint')}
            value={form.avoid ?? ''}
            onChange={(event) => change('avoid', event.target.value)}
          />
        </label>
      </section>

      {/* --------------------------------------------- how the pictures look */}
      {/* Their own pictures, next to where their website is read from: both answer the same
          question, which is where the material for a post comes from. */}
      <ContentSets onPostsWritten={onPostsWritten} />

      {/* What kind of business this is, which is really a question about what to call things. One
          row, two answers, and everything downstream reads it — the portal's words and the model's. */}
      <section className="card">
        <h2>{t('profile.kindTitle')}</h2>
        <p className="muted">{t('profile.kindHelp')}</p>

        <div className="kinds">
          {['Shop', 'Creator'].map((kind) => (
            <label key={kind} className={`kind${(form.kind ?? 'Shop') === kind ? ' is-on' : ''}`}>
              <input
                type="radio"
                name="kind"
                value={kind}
                checked={(form.kind ?? 'Shop') === kind}
                onChange={() => setForm({ ...form, kind })}
              />
              <span className="kind__name">{t(`profile.kinds.${kind}`)}</span>
              <span className="muted small">{t(`profile.kinds.${kind}Help`)}</span>
            </label>
          ))}
        </div>
      </section>

      <section className="card">
        <h2>{t('profile.lookTitle')}</h2>
        <p className="muted">{t('profile.lookHelp')}</p>

        <div className="looks__grid">
          {['Clean', 'Bold', 'Night'].map((look) => (
            <label key={look} className={`look${(form.imageTemplate ?? 'Clean') === look ? ' is-on' : ''}`}>
              <input
                type="radio"
                name="imageTemplate"
                value={look}
                checked={(form.imageTemplate ?? 'Clean') === look}
                onChange={() => change('imageTemplate', look)}
              />
              {/* Drawn on the server in this business's colour, with its mark: what their posts
                  will look like, not what a sample would. The colour in the query busts the
                  browser's cache when they change it. A picture that will not load leaves the
                  frame it was going to fill, not a broken-image mark. */}
              <img
                className="look__preview"
                src={`/api/profile/looks/${look}/preview?c=${encodeURIComponent(form.brandColorHex ?? '')}`}
                alt=""
                loading="lazy"
                onError={(event) => {
                  event.currentTarget.style.visibility = 'hidden'
                }}
              />
              <span className="look__name">{t(`profile.looks.${look}`)}</span>
              <span className="muted small">{t(`profile.looks.${look}Help`)}</span>
            </label>
          ))}
        </div>

        <div className="fields fields--two">
          <label className="field">
            {t('profile.imageText')}
            <span className="muted small">{t('profile.imageTextHelp')}</span>
            <select
              value={form.imageTextStyle ?? 'Highlight'}
              onChange={(event) => change('imageTextStyle', event.target.value)}
            >
              <option value="Highlight">{t('profile.imageTextStyle.Highlight')}</option>
              <option value="Transparent">{t('profile.imageTextStyle.Transparent')}</option>
              <option value="None">{t('profile.imageTextStyle.None')}</option>
            </select>
          </label>

          <label className="field">
            {t('profile.aiImages')}
            <span className="muted small">{t('profile.aiImagesHelp')}</span>
            <select value={form.aiImageMode ?? 'Backdrop'} onChange={(event) => change('aiImageMode', event.target.value)}>
              <option value="Backdrop">{t('profile.aiImageMode.Backdrop')}</option>
              <option value="Product">{t('profile.aiImageMode.Product')}</option>
              <option value="Off">{t('profile.aiImageMode.Off')}</option>
            </select>
          </label>
        </div>

        {form.aiImageMode === 'Product' && <p className="status status--warn">{t('profile.aiImagesWarning')}</p>}

        <div className="fields fields--two">
          <div className="field">
            {t('profile.logo')}
            <span className="muted small">{t('profile.logoHelp')}</span>

            <div className="row row--form">
              {form.logoUrl && <img className="logo-preview" src={form.logoUrl} alt="" />}

              <label className="button button--quiet upload">
                {t('profile.logoUpload')}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  disabled={busy}
                  onChange={async (event) => {
                    const file = event.target.files?.[0]
                    event.target.value = ''
                    if (!file) return
                    setBusy(true)
                    setError(null)
                    try {
                      const result = await api.uploadLogo(file)
                      setForm((current) => ({ ...current, logoUrl: result.logoUrl }))
                    } catch (failure) {
                      setError(failure.message)
                    } finally {
                      setBusy(false)
                    }
                  }}
                />
              </label>
            </div>
          </div>

          <label className="field">
            {t('profile.color')}
            <span className="muted small">{t('profile.colorHelp')}</span>
            <input
              type="color"
              className="swatch"
              value={form.brandColorHex ?? '#1B4965'}
              onChange={(event) => change('brandColorHex', event.target.value.toUpperCase())}
            />
          </label>
        </div>
      </section>

      {/* ------------------------------------------ what every post carries */}
      <section className="card">
        <h2>{t('profile.postsTitle')}</h2>
        <p className="muted">{t('profile.postsHelp')}</p>

        {toggle('includeStories', true)}
        {toggle('includeVideos', false)}

        <label className="field">
          {t('profile.carousel')}
          <span className="muted small">{t('profile.carouselHelp')}</span>
          <select
            value={form.carouselImages ?? 5}
            onChange={(event) => change('carouselImages', Number(event.target.value))}
          >
            <option value={1}>{t('profile.carouselOff')}</option>
            {[3, 5, 10].map((count) => (
              <option key={count} value={count}>
                {t('profile.carouselUpTo', { count })}
              </option>
            ))}
          </select>
        </label>
      </section>

      {/* The one Save for all three, following down the page: whatever was just changed, the
          button that keeps it is at the bottom of the screen rather than the bottom of the form. */}
      {(dirty || saved || error) && (
        <div className="savebar">
          <span className="savebar__state">
            {error ? (
              <span className="status status--bad">{error}</span>
            ) : dirty ? (
              t('profile.unsaved')
            ) : (
              <span className="status status--good">{t('profile.saved')}</span>
            )}
          </span>

          {dirty && (
            <Button type="button" className="button" disabled={!form.name} loading={busy} onClick={save}>
              {t('profile.save')}
            </Button>
          )}
        </div>
      )}
    </>
  )
}
